import * as React from 'react'

import { reaction } from 'mobx'
import { useFormContext, useWatch } from 'react-hook-form'

import MapViewSetUpStore from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/MapViewSetUp.store'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import IHierarchyParent from '~/client/src/shared/types/IHierarchyParent'

interface IProps {
  store: MapViewSetUpStore
}

const norm = (value: unknown): unknown => value ?? null

// By id alone, as LocationBase.isParent: the form (seeded from the Apollo record) and the map
// item (from the store) can carry the same parent as differently shaped objects, and reading
// that as an edit pushed the form parent onto the item and reset its accessible levels for
// nothing (STX1-1187). Only the parent's id is sent (STX1-1355).
export const isSameParent = (
  a?: IHierarchyParent | null,
  b?: IHierarchyParent | null,
): boolean => {
  if (!a && !b) return true
  if (!a || !b) return false
  return a.parentId === b.parentId
}

/**
 * Mirrors the icon / color / parent between the General tab (the resource form,
 * seeded from the Apollo record, or from the drawn item when creating) and the
 * live MobX map item that the Map tab (PropertiesPanel) edits. Mounted through
 * the card's formSync slot, so it runs in creation too — where the Map section is
 * hidden — and the drawn item previews a pick before Create. Without it an edit on
 * one tab is invisible on the other, and the shared footer Save reapplies the
 * form's values over any map-tab edits. Equality guards keep the two directions
 * from looping.
 */
export const MapResourceFieldSync: React.FC<IProps> = ({ store }) => {
  const { getValues, setValue } = useFormContext()
  const item = store.mapViewItemsSetupStore.selectedMapViewItem

  const [iconName, color, parent] = useWatch({
    name: ['iconName', 'color', 'parent'],
  }) as [MapViewLocationIcon | null, string | null, IHierarchyParent | null]

  // Form -> map item. Push a General-tab edit onto the live map item as soon as the
  // form value changes, for as long as the card is open — including a change back to
  // the value the card opened with, which RHF no longer counts as dirty.
  //
  // On a saved item the `color` field is the ICON color only, so it syncs with setColor
  // (NOT setAllColors): the shape fill/line colors are edited independently on the Map
  // tab and must be preserved. While creating, the Map tab is hidden and Create paints
  // the shape with the form color, so the preview does too - a location created from a
  // tileset shape already has one.
  //
  // Three guards keep this safe:
  //  - changed since the last run: the first run only records the form values, so
  //    a fresh open never mutates the item (which would trip
  //    safelyDeselectMapViewItem and pop the confirm dialog on an unchanged close);
  //  - value-differs: no ping-pong with the item->form effect below;
  //  - same item instance: undo/redo swaps in a fresh item (same id) with the
  //    undone value; re-pushing the form value onto it would cancel the undo, so on
  //    a swap we defer to the item->form effect and only record.
  // A passive effect, declared before the item->form effect below so it runs first. Not a
  // layout effect: a pushed icon or colour rebuilds the whole marker source and rasterises
  // the new image, which in a layout effect blocked the paint of the field the user just
  // changed (the picker closing, the trigger showing the new icon) for up to a second.
  const prevRef = React.useRef<{
    item: typeof item
    iconName: MapViewLocationIcon | null
    color: string | null
    parent: IHierarchyParent | null
  } | null>(null)
  React.useEffect(() => {
    if (!item) return
    const prev = prevRef.current
    prevRef.current = { item, iconName, color, parent }
    if (!prev || prev.item !== item) return
    if (
      norm(color) !== norm(prev.color) &&
      color &&
      norm(color) !== norm(item.color)
    ) {
      if (item.id) {
        item.setColor(color)
      } else {
        item.setAllColors(color)
      }
    }
    if (
      norm(iconName) !== norm(prev.iconName) &&
      iconName != null &&
      norm(iconName) !== norm(item.iconName)
    ) {
      item.updateIcon(iconName)
    }
    if (
      !isSameParent(parent, prev.parent) &&
      !isSameParent(parent, item.parent)
    ) {
      item.setParent(parent ?? null)
      // Pair with resetAccessibleLevels, as every other setParent call site does.
      store.mapViewItemsSetupStore.resetAccessibleLevels(item)
    }
  }, [item, iconName, color, parent, store])

  // Map item -> form. Mirror the live map item into the form WITHOUT dirtying it
  // (the map item's own changed-state drives Save/close on the maps view). Runs
  // on mount, on undo/redo item-instance swaps (so the form follows the undone
  // value), and on live PropertiesPanel edits via the reaction.
  React.useEffect(() => {
    if (!item) return
    const syncToForm = (source: {
      iconName: MapViewLocationIcon | null
      color: string | null
      parent: IHierarchyParent | null
    }): void => {
      const [formIcon, formColor, formParent] = getValues([
        'iconName',
        'color',
        'parent',
      ]) as [MapViewLocationIcon | null, string | null, IHierarchyParent | null]
      if (norm(source.color) !== norm(formColor)) {
        setValue('color', source.color ?? null)
      }
      if (norm(source.iconName) !== norm(formIcon)) {
        setValue('iconName', source.iconName ?? null)
      }
      if (!isSameParent(source.parent, formParent)) {
        setValue('parent', source.parent ?? null)
      }
    }
    syncToForm({
      iconName: item.iconName,
      color: item.color,
      parent: item.parent,
    })
    return reaction(
      () => ({
        iconName: item.iconName,
        color: item.color,
        parent: item.parent,
      }),
      syncToForm,
    )
  }, [item, getValues, setValue])

  return null
}
